import React, { useEffect, useId, useState } from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { CheckCircleIcon, EnvelopeIcon, UserPlusIcon } from '@heroicons/react/24/outline'
import { OrgRole } from 'pure-js/types/OrganizationMembership'

import { useCreateOrgMemberByEmail, useInviteUserToOrganization } from '../../../apiLayer/queryHooks/mutations'
import { Button, Input, Spinner } from '../../../interaxDesignSystemComponents'
import { DefaultModal } from '../../../shared/components/DefaultModal'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { orgRoleTranslationKey } from '../orgTranslationKeys'
import { OrgInviteRolePicker } from './OrgInviteRolePicker'

interface OrgInviteModalProps {
  isOpen: boolean
  onClose: () => void
  organizationId: string | undefined
  canSelectRole: boolean
  isLeaderRoleOffered: boolean
  memberCount: number
  maxEmployees: number | undefined
  pendingInviteCount: number
}

type ModalPhase = 'form' | 'success'
type SuccessMode = 'invite' | 'create'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const fieldLabelSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: INTERAX_COLORS.white70,
  mb: 1,
  letterSpacing: 0.2
} as const

function resolveInviteErrorKey(error: unknown): string {
  const message =
    error && typeof error === 'object' && 'message' in error ? String((error as { message?: string }).message) : ''
  if (message.includes('org-capacity-exceeded') || message.includes('resource-exhausted')) {
    return 'org.team.invite.errors.capacity'
  }
  if (message.includes('facilitator-may-only-invite-gamer')) {
    return 'org.team.invite.errors.facilitatorRole'
  }
  if (message.includes('already-org-member')) {
    return 'org.team.invite.errors.alreadyMember'
  }
  return 'org.members.invite.error'
}

/**
 * Invite-by-email modal for the organization team page.
 */
export const OrgInviteModal: React.FC<OrgInviteModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  canSelectRole,
  isLeaderRoleOffered,
  memberCount,
  maxEmployees,
  pendingInviteCount
}) => {
  const { t } = useTranslation()
  const titleId = useId()
  const descriptionId = useId()
  const seatsId = useId()
  const inviteUser = useInviteUserToOrganization()
  const createMember = useCreateOrgMemberByEmail()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<OrgRole>(OrgRole.GAMER)
  const [phase, setPhase] = useState<ModalPhase>('form')
  const [successMode, setSuccessMode] = useState<SuccessMode>('invite')
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [emailTouched, setEmailTouched] = useState(false)

  const trimmedEmail = email.trim()
  const isEmailValid = EMAIL_PATTERN.test(trimmedEmail)
  const showEmailError = emailTouched && trimmedEmail.length > 0 && !isEmailValid
  const seatsUsed = memberCount + pendingInviteCount
  const atCapacity = maxEmployees != null && seatsUsed >= maxEmployees
  const isPending = inviteUser.isPending || createMember.isPending

  const resetForm = () => {
    setEmail('')
    setRole(OrgRole.GAMER)
    setPhase('form')
    setSuccessMode('invite')
    setErrorKey(null)
    setEmailTouched(false)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  useEffect(() => {
    if (!isOpen) resetForm()
  }, [isOpen])

  const inviteRole = canSelectRole ? role : OrgRole.GAMER

  const submitInvite = () => {
    setEmailTouched(true)
    if (!trimmedEmail || !organizationId || !isEmailValid || atCapacity) return
    setErrorKey(null)
    inviteUser.mutate(
      { organizationId, email: trimmedEmail, role: inviteRole },
      {
        onSuccess: () => {
          setSuccessMode('invite')
          setPhase('success')
        },
        onError: (err) => setErrorKey(resolveInviteErrorKey(err))
      }
    )
  }

  const submitCreate = () => {
    setEmailTouched(true)
    if (!trimmedEmail || !organizationId || !isEmailValid || atCapacity) return
    setErrorKey(null)
    createMember.mutate(
      { organizationId, email: trimmedEmail, role: inviteRole },
      {
        onSuccess: () => {
          setSuccessMode('create')
          setPhase('success')
        },
        onError: (err) => setErrorKey(resolveInviteErrorKey(err))
      }
    )
  }

  const addAnother = () => {
    setEmail('')
    setRole(OrgRole.GAMER)
    setPhase('form')
    setSuccessMode('invite')
    setErrorKey(null)
    setEmailTouched(false)
  }

  const canSubmit = !isPending && !!trimmedEmail && isEmailValid && !!organizationId && !atCapacity

  const successTitleKey =
    successMode === 'create' ? 'org.team.invite.createSuccessTitle' : 'org.team.invite.successTitle'
  const successBodyKey = successMode === 'create' ? 'org.team.invite.createSuccess' : 'org.team.invite.success'
  const addAnotherLabelKey =
    successMode === 'create' ? 'org.team.invite.createAnother' : 'org.team.invite.inviteAnother'

  return (
    <DefaultModal isOpen={isOpen} onClose={handleClose} maxWidth="520px" disableBackdropClose>
      <Stack spacing={0} role="dialog" aria-labelledby={titleId} aria-describedby={`${descriptionId} ${seatsId}`}>
        <Stack spacing={{ xs: 2, sm: 2.5, md: 2.5 }} sx={{ pb: { xs: 2, sm: 2.5, md: 2.5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                mt: 0.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '10px',
                flexShrink: 0,
                backgroundColor: INTERAX_COLORS.sun10,
                border: `1px solid ${INTERAX_COLORS.sun20}`
              }}
            >
              <UserPlusIcon style={{ width: 22, height: 22, color: INTERAX_COLORS.sun }} />
            </Box>
            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                id={titleId}
                sx={{ fontSize: { xs: 18, sm: 20, md: 20 }, fontWeight: 600, color: INTERAX_COLORS.white }}
              >
                {t('org.members.invite.title')}
              </Typography>
              <Typography
                id={descriptionId}
                sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey, lineHeight: 1.5 }}
              >
                {t('org.team.invite.description')}
              </Typography>
            </Stack>
          </Stack>

          <Box
            id={seatsId}
            sx={{
              px: { xs: 1.5, sm: 2, md: 2 },
              py: { xs: 1.25, sm: 1.5, md: 1.5 },
              borderRadius: 1,
              border: `1px solid ${INTERAX_COLORS.white10}`,
              backgroundColor: INTERAX_COLORS.offBlack
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: INTERAX_COLORS.white70 }}>
                {t('org.team.invite.seatsLabel')}
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: INTERAX_COLORS.white }}>
                {maxEmployees != null
                  ? t('org.team.invite.seatsCount', { used: seatsUsed, max: maxEmployees })
                  : t('org.team.invite.seatsCountUnknown', { used: seatsUsed })}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 12, color: INTERAX_COLORS.thunderGrey, mt: 0.5 }}>
              {t('org.team.invite.seatsCaption')}
            </Typography>
            {atCapacity && (
              <Typography sx={{ fontSize: 12, color: INTERAX_COLORS.sun, mt: 0.75, fontWeight: 600 }}>
                {t('org.team.invite.atCapacity')}
              </Typography>
            )}
          </Box>

          {phase === 'success' ? (
            <Stack spacing={2} alignItems="center" sx={{ py: { xs: 1, sm: 2, md: 2 }, textAlign: 'center' }}>
              <CheckCircleIcon style={{ width: 48, height: 48, color: INTERAX_COLORS.green }} aria-hidden />
              <Stack spacing={0.75}>
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: INTERAX_COLORS.white }}>
                  {t(successTitleKey)}
                </Typography>
                <Typography sx={{ fontSize: 14, color: INTERAX_COLORS.thunderGrey, lineHeight: 1.5 }}>
                  {t(successBodyKey, { email: trimmedEmail })}
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={{ xs: 2, sm: 2.5, md: 2.5 }}>
              <Box>
                <Typography sx={fieldLabelSx}>{t('org.members.invite.emailLabel')}</Typography>
                <Input
                  type="email"
                  fullWidth
                  autoFocus
                  placeholder={t('org.members.invite.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  onEnterPress={submitInvite}
                  error={showEmailError}
                  errorMessage={showEmailError ? t('org.team.invite.emailInvalid') : undefined}
                  startIcon={<EnvelopeIcon style={{ width: 18, height: 18, color: INTERAX_COLORS.white70 }} />}
                  data-testid="org-invite-modal-email"
                />
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>{t('org.members.invite.roleLabel')}</Typography>
                {canSelectRole ? (
                  <OrgInviteRolePicker value={role} onChange={setRole} isLeaderRoleOffered={isLeaderRoleOffered} />
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Chip
                      label={t(orgRoleTranslationKey(OrgRole.GAMER))}
                      size="small"
                      sx={{
                        backgroundColor: INTERAX_COLORS.sun20,
                        color: INTERAX_COLORS.white,
                        fontWeight: 600,
                        border: `1px solid ${INTERAX_COLORS.sun40}`
                      }}
                    />
                    <Typography sx={{ fontSize: 13, color: INTERAX_COLORS.thunderGrey }}>
                      {t('org.team.invite.facilitatorRoleHint')}
                    </Typography>
                  </Stack>
                )}
              </Box>

              {errorKey && (
                <Box
                  role="alert"
                  sx={{
                    px: { xs: 1.5, sm: 2, md: 2 },
                    py: 1.25,
                    borderRadius: 1,
                    backgroundColor: INTERAX_COLORS.redErrorBackground,
                    border: `1px solid ${INTERAX_COLORS.redError}`
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: INTERAX_COLORS.redError }}>{t(errorKey)}</Typography>
                </Box>
              )}
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
          sx={{
            pt: { xs: 2, sm: 2, md: 2 },
            borderTop: `1px solid ${INTERAX_COLORS.white10}`
          }}
        >
          {phase === 'success' ? (
            <>
              <Button variant="secondary" size="medium" onClick={handleClose}>
                {t('org.team.invite.done')}
              </Button>
              <Button variant="primary" size="medium" onClick={addAnother}>
                {t(addAnotherLabelKey)}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" size="medium" onClick={handleClose} disabled={isPending}>
                {t('org.members.invite.cancel')}
              </Button>
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={submitCreate}
                  disabled={!canSubmit}
                  style={!canSubmit && !createMember.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  data-testid="org-invite-modal-create"
                >
                  {createMember.isPending ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Spinner size="small" />
                      <span>{t('org.team.invite.creating')}</span>
                    </Stack>
                  ) : (
                    t('org.team.invite.createLabel')
                  )}
                </Button>
                <Button
                  variant="primary"
                  size="medium"
                  onClick={submitInvite}
                  disabled={!canSubmit}
                  style={!canSubmit && !inviteUser.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  data-testid="org-invite-modal-submit"
                >
                  {inviteUser.isPending ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Spinner size="small" />
                      <span>{t('org.team.invite.sending')}</span>
                    </Stack>
                  ) : (
                    t('org.team.invite.submitLabel')
                  )}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
    </DefaultModal>
  )
}
